import {
    AssetInner,
    AuctionDetail,
    LotRowImage,
    WatchedItemsFlattenedListResponse,
} from '@viewbid/ts-angular-client';
import { Auction, Lot, LotImage } from '@viewbid/ui';
import { wireRead } from './wire-read.util';

/**
 * Reader-output seam for lot rows: a canonical wire row (e.g.
 * AuctionLotHighlightedListItem) after normalizeLotRow has collapsed the wire
 * aliases onto the legacy field names the pages read and mutate (socket
 * patches, optimistic bid state). Canonical list rows are assignable to it, so
 * it also types normalizeLotRow's input.
 */
export interface LotResponseWithProxy {
    id?: string;
    lotId?: string;
    auctionId?: string;
    auctionLotsId?: string;
    number?: number | null;
    suffix?: string;
    title?: string;
    condition?: string;
    category?: string | null;
    images?: Array<AssetInner>;
    thumbnailId?: string | null;
    bidCount?: number;
    numberOfWatchers?: number;
    currentPrice?: number;
    bidIncrement?: number;
    minBidAmount?: number;
    reserveAmount?: number;
    highBidderId?: string | null;
    watcherId?: string | null;
    status?: string;
    isAcceptOffers?: boolean;
    toDatetime?: string;
    userMaxBid?: number | null;
    maximumProxyBid?: number;
}

export interface WatchedItemWithProxy extends Omit<
    WatchedItemsFlattenedListResponse,
    'images'
> {
    maximumProxyBid?: number;
    /** Contract array when includeImages was sent; legacy imageURL string as fallback until the old wire dies. */
    images?: Array<LotRowImage> | string | null;
    thumbnailId?: string | null;
}

const NO_IMAGE_FALLBACK: string = '/assets/img/brand/no-image-lot.svg';

function toLotImages(images?: Array<AssetInner> | null): LotImage[] {
    const mapped: LotImage[] =
        images
            ?.filter((asset: AssetInner): asset is AssetInner => !!asset?.url)
            .map(({ id, url }: AssetInner) => ({ id, url })) ?? [];
    return mapped.length ? mapped : [{ url: NO_IMAGE_FALLBACK }];
}

function watchedItemImages(images?: string | null): LotImage[] {
    return [{ url: images || NO_IMAGE_FALLBACK }];
}

// The API and socket payloads sometimes return numeric money fields as strings.
// Coerce here so downstream arithmetic uses numbers, not string concatenation.
function asNumber(value: unknown): number | undefined {
    if (value === null || value === undefined || value === '') return undefined;
    const n: number = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(n) ? n : undefined;
}

// The canonical wire sends UPPERCASE auction-lot statuses while the UI
// library's card lifecycle compares against the legacy Title Case LotStatus
// values ('Complete', 'Not Sold', ...). Normalize whichever wire key won so
// closed lots still render as sold/not-sold. Canonical SOLD has no legacy
// twin; Complete is the UI value that renders a sold card.
const UI_LOT_STATUS: Record<string, string> = {
    PUBLISHED: 'Published',
    DRAFT: 'Draft',
    COMPLETE: 'Complete',
    ARCHIVED: 'Archived',
    SOLD: 'Complete',
    'NOT SOLD': 'Not Sold',
    NOT_SOLD: 'Not Sold',
};

function toUiLotStatus(status: string | undefined): string | undefined {
    if (!status) return undefined;
    return UI_LOT_STATUS[status.toUpperCase()] ?? status;
}

/**
 * Collapses canonical/legacy wire aliases on a lot row into the legacy field
 * names the pages read and mutate (socket patches, optimistic bid state), so a
 * stale canonical alias can never shadow a later legacy write. Dies with
 * wireRead when the canonical client lands.
 */
export function normalizeLotRow(
    row: LotResponseWithProxy,
): LotResponseWithProxy {
    const rest: Record<string, unknown> = {
        ...(row as Record<string, unknown>),
    };
    delete rest['auctionLotStatus'];
    delete rest['currentBid'];
    delete rest['currentHighBidderId'];
    delete rest['thumbnailUrl'];
    const thumbnailUrl: string | undefined = wireRead<string>(
        row,
        'thumbnailUrl',
    );
    return {
        ...(rest as LotResponseWithProxy),
        auctionLotsId: row.auctionLotsId ?? row.id,
        status: toUiLotStatus(
            wireRead<string>(row, 'auctionLotStatus', 'status'),
        ),
        currentPrice: wireRead<number>(row, 'currentBid', 'currentPrice'),
        highBidderId: wireRead<string>(
            row,
            'currentHighBidderId',
            'highBidderId',
        ),
        images: row.images ?? (thumbnailUrl ? [{ url: thumbnailUrl }] : []),
    };
}

export function lotResponseToLot(response: LotResponseWithProxy): Lot {
    // Canonical-vs-legacy wire aliases (lot DTO cutover): read canonical first,
    // fall back to legacy. Collapses when the canonical client lands.
    const rawCurrentPrice: unknown = wireRead(
        response,
        'currentBid',
        'currentPrice',
    );
    const thumbnailUrl: string | undefined = wireRead<string>(
        response,
        'thumbnailUrl',
    );
    const currentPrice: number = asNumber(rawCurrentPrice) ?? 0;
    const minBid: number = asNumber(response.minBidAmount) ?? 0;
    // The card derives the quick-bid value as currentPrice + bidIncrement.
    // Socket updates refresh currentPrice/minBidAmount but not the raw
    // increment, so derive it from the authoritative minBid to keep the
    // quick bid correct after live price changes (e.g. proxy outbids).
    const bidIncrement: number =
        currentPrice > 0 && minBid > currentPrice
            ? minBid - currentPrice
            : (asNumber(response.bidIncrement) ?? 0);

    return {
        auctionLotsId: response.auctionLotsId ?? response.id,
        id: response.id,
        lotId: response.lotId,
        auctionId: response.auctionId,
        number: response.number ?? undefined,
        suffix: response.suffix,
        title: response.title ?? '',
        condition: response.condition,
        category: response.category ?? undefined,
        // Detail rows carry the full asset list next to the derived
        // thumbnailUrl; collapsing onto the thumbnail would strip the gallery
        // down to one photo. Prefer the full list, and use the thumbnail only
        // for list rows that carry no assets (matches normalizeLotRow).
        images: response.images?.length
            ? toLotImages(response.images)
            : thumbnailUrl
              ? [{ url: thumbnailUrl }]
              : toLotImages(response.images),
        thumbnailId: response.thumbnailId ?? undefined,
        bidCount: asNumber(response.bidCount),
        watchers: asNumber(response.numberOfWatchers),
        currentPrice: asNumber(rawCurrentPrice),
        bidIncrement,
        minBidAmount: asNumber(response.minBidAmount),
        reserveAmount: asNumber(response.reserveAmount),
        highBidderId: wireRead<string>(
            response,
            'currentHighBidderId',
            'highBidderId',
        ),
        watcherId: response.watcherId,
        status: toUiLotStatus(
            wireRead<string>(response, 'auctionLotStatus', 'status'),
        ),
        isAcceptOffers: response.isAcceptOffers,
        maximumProxyBid:
            asNumber(response.maximumProxyBid) ?? asNumber(response.userMaxBid),
    };
}

export function watchedItemToLot(item: WatchedItemWithProxy): Lot {
    const currentBid: number = asNumber(item.currentBid) ?? 0;
    const minBid: number = asNumber(item.minimumBidAmount) ?? 0;
    const derivedIncrement: number =
        currentBid > 0 && minBid > currentBid ? minBid - currentBid : 0;

    return {
        auctionLotsId: item.auctionLotId,
        lotId: item.lotId,
        auctionId: item.auctionId,
        number: item.lotNumber,
        suffix: item.suffix,
        title: item.title ?? '',
        condition: item.condition,
        category: item.category ?? undefined,
        images: Array.isArray(item.images)
            ? toLotImages(item.images)
            : watchedItemImages(item.images),
        thumbnailId: item.thumbnailId ?? undefined,
        currentPrice: currentBid,
        bidIncrement: derivedIncrement,
        minBidAmount: minBid,
        reserveAmount: asNumber(item.reserveAmount),
        highBidderId: item.currentHighBidderId ?? undefined,
        watcherId: item.watcherId,
        status: toUiLotStatus(item.lotStatus),
        isAcceptOffers: item.isAcceptOffers,
        maximumProxyBid:
            asNumber(item.maximumProxyBid) ?? asNumber(item.userMaxBid),
    };
}

export function auctionResponseToAuction(auction: AuctionDetail): Auction {
    return {
        id: auction.id,
        status: auction.status,
        buyerPremium: asNumber(auction.buyerPremium),
        isProxyBid: auction.isProxyBiddingEnabled,
        isDisplayFinalPrice: auction.isDisplayFinalPrice,
        isMakeOffer: auction.isMakeOfferEnabled,
        fromDatetime: auction.fromDatetime,
    };
}

export function watchedItemToAuction(item: WatchedItemWithProxy): Auction {
    return {
        id: item.auctionId,
        status: item.auctionStatus,
        buyerPremium: asNumber(item.buyerPremium),
        isProxyBid: item.isProxyBiddingEnabled,
        isDisplayFinalPrice: item.isDisplayFinalPrice,
        isMakeOffer: item.isAcceptOffers,
        fromDatetime: item.fromDatetime,
    };
}

export interface AuctionUpdateResult {
    items: WatchedItemWithProxy[];
    changed: boolean;
}

export function applyAuctionUpdateToWatchedItems(
    items: WatchedItemWithProxy[],
    detail: AuctionDetail,
): AuctionUpdateResult {
    let changed: boolean = false;
    const next: WatchedItemWithProxy[] = items.map(
        (item: WatchedItemWithProxy) => {
            if (item.auctionId !== detail.id) {
                return item;
            }
            changed = true;
            return {
                ...item,
                auctionStatus: detail.status,
                buyerPremium: detail.buyerPremium,
                isProxyBiddingEnabled: detail.isProxyBiddingEnabled,
                isDisplayFinalPrice: detail.isDisplayFinalPrice,
                isAcceptOffers: detail.isMakeOfferEnabled,
                fromDatetime: detail.fromDatetime,
            };
        },
    );
    return { items: next, changed };
}

export function lotImageUrlOrFallback(lot: Lot): string {
    const first: LotImage | undefined = lot.images?.[0];
    return first?.url ?? NO_IMAGE_FALLBACK;
}
